/**
 * Hand-written editorial content layered on top of the live OpenAPI document.
 *
 * The contract itself (endpoints, parameters, required fields, types) always comes from the
 * spec — see parse-openapi.ts. An overlay only pins a stable route id and adds curated prose
 * and examples for endpoints we have documented by hand. Endpoints without an overlay still
 * render, fully derived from the spec.
 */
export interface EndpointOverlay {
  /** Stable route id — keeps /api/:endpointId URLs and legacy redirects working. */
  readonly id: string;
  readonly title?: string;
  readonly examplePath?: string;
  readonly description?: string;
  readonly returns?: string;
  readonly requestBodyExample?: object;
  readonly responseExample?: object;
}

const SAMPLE_ADDRESS = {
  locationId: '1f7d2a3c-4b5e-4f6a-8b9c-0d1e2f3a4b5c',
  locationName: 'NewCold Wakefield',
  street: 'Barmoor Way',
  number: '1',
  city: 'Wakefield',
  zipCode: 'WF10 5UB',
  country: 'United Kingdom',
  countryCode: 'GB',
  timeZoneId: 'Europe/London',
};

/** Keyed by `METHOD /path`, exactly as the path appears in the OpenAPI document. */
export const ENDPOINT_OVERLAYS: Readonly<Record<string, EndpointOverlay>> = {
  'GET /api/v1/User/get-booking-users': {
    id: 'users-get-booking-users',
    title: 'Get booking users',
    description:
      'Returns all users within your own enterprise that hold the Booker role, grouped per (sub)group. Use the groupId and userId from this response when placing a booking.',
    returns:
      'Groups in your enterprise, each with the users that have booking permission. Both a groupId and a userId are required to place a booking.',
    responseExample: {
      groups: [
        {
          groupId: '8c2e1b4a-9d3f-4c5e-a6b7-c8d9e0f1a2b3',
          groupName: 'CtrlChain Europe',
          users: [
            {
              userId: 'b9b0054f-000f-4e0d-a529-555f31821541',
              username: 'planner@yourco.com',
              firstName: 'Sam',
              lastName: 'Planner',
            },
          ],
        },
      ],
    },
  },
  'GET /api/v1/Lane/get-lanes': {
    id: 'lanes-get-lanes',
    title: 'Get lanes',
    description:
      'Returns your contracted lanes. Only lanes that are currently valid are included — if a lane is missing, contact your account manager.',
    returns:
      'A list of lane objects with their locations, vehicle and trailer type, contacts, and the agreed rates per validity period. Pass the laneId when requesting a booking.',
    responseExample: {
      lanes: [
        {
          laneId: '5a1c9e7d-2b3f-4a6c-8d9e-0f1a2b3c4d5e',
          laneReferenceNumber: 'LANE-2026-0118',
          pickupAddress: SAMPLE_ADDRESS,
          deliveryAddress: {
            locationId: '9e8d7c6b-5a4f-4e3d-b2c1-a0f9e8d7c6b5',
            locationName: 'Tesco DC Daventry',
            city: 'Daventry',
            countryCode: 'GB',
          },
          vehicleType: {
            vehicleTypeId: '3d4e5f6a-7b8c-4d9e-a0b1-c2d3e4f5a6b7',
            vehicleTypeName: 'Truck',
          },
          trailerType: {
            trailerTypeId: '2c3d4e5f-6a7b-4c8d-9e0f-1a2b3c4d5e6f',
            trailerTypeName: 'Reefer',
          },
          contacts: [],
          rates: [{ startDate: '2026-01-01', endDate: '2026-12-31', rate: 500 }],
        },
      ],
    },
  },
  'POST /api/v1/Request/lane-booking': {
    id: 'requests-lane-booking',
    title: 'Request a lane booking',
    description:
      'Places a booking on a lane. The platform validates your input and, when all fields are correct, immediately generates an order. Bookings are created one at a time — for recurring orders, call this endpoint once per shipment.',
    returns:
      'The reference numbers of the created request and order, plus the resolved pickup and delivery addresses and dates. Invalid input fails with 400 and a problem details body.',
    requestBodyExample: {
      laneId: '5a1c9e7d-2b3f-4a6c-8d9e-0f1a2b3c4d5e',
      groupId: '8c2e1b4a-9d3f-4c5e-a6b7-c8d9e0f1a2b3',
      userId: 'b9b0054f-000f-4e0d-a529-555f31821541',
      tailLift: false,
      desiredPickupDateTimes: {
        earliest: '2026-08-04T06:00:00',
        latest: '2026-08-04T10:00:00',
      },
      references: { shipper: 'PO-2026-118', pickup: '', delivery: '' },
    },
    responseExample: {
      ccaRequestReferenceNumber: 'CCA2026-007873',
      ccaOrderReferenceNumber: 'CCA2026-007873.1',
      pickupAddress: SAMPLE_ADDRESS,
      deliveryAddress: { locationName: 'Tesco DC Daventry', city: 'Daventry', countryCode: 'GB' },
      pickupDateTime: { earliest: '2026-08-04T06:00:00', latest: '2026-08-04T10:00:00' },
      deliveryDateTime: { earliest: '2026-08-04T14:00:00', latest: '2026-08-04T18:00:00' },
    },
  },
  'GET /api/v1/Order/detail/{orderId}': {
    id: 'orders-detail',
    title: 'Get order details',
    examplePath: '/api/v1/Order/detail/b9b6854f-282f-4e0d-a529-555f23441541',
    description:
      'Returns the details of a specific order: status, stops, carrier and driver details, and approved PODs. Webhook notifications carry a DetailUrl that points at this endpoint.',
    returns:
      'The full order object, including stops with requested/expected/arrival dates and, once uploaded and approved, PODs with a download link.',
    responseExample: {
      orderId: 'b9b6854f-282f-4e0d-a529-555f23441541',
      referenceNumber: 'CCA2026-007873.1',
      shipperReference: 'PO-2026-118',
      shipperGroupName: 'CtrlChain Europe',
      carrierGroupName: 'Arctic Line BV',
      bookerUserName: 'planner@yourco.com',
      orderStatus: 'InTransit',
      podStatus: 'Pending',
      approvedPODs: [],
      stops: [
        {
          stopReference: 'PO-2026-118',
          address: SAMPLE_ADDRESS,
          requestedDate: '2026-08-04T06:00:00',
          expectedDate: '2026-08-04T07:15:00',
        },
      ],
    },
  },
  'GET /api/v1/Order/file/download/{fileId}': {
    id: 'orders-file-download',
    title: 'Download an order file',
    examplePath: '/api/v1/Order/file/download/7f2b3c4d-5e6f-4a8b-9c0d-1e2f3a4b5c6d',
    description:
      'Downloads a file attached to an order, such as an approved POD. File ids and ready-made download URLs are available on the order detail response once a POD has been approved.',
    returns: 'The file contents. Fails with 400 or 401 and a problem details body.',
  },
  'POST /api/v1/Webhook/paginated': {
    id: 'webhooks-paginated',
    title: 'List triggered webhooks',
    description:
      'Returns the webhooks that have been triggered for your enterprise, based on the pagination parameters.',
    returns:
      'A paginated set of triggered webhook events, including the delivery response status your endpoint returned. Use the eventId to retrigger a delivery.',
    requestBodyExample: { pageIndex: 0, pageSize: 25 },
    responseExample: {
      totalItems: 1,
      items: [
        {
          eventId: 'bc4fff3e-cfc3-0000-8d5d-000b12310e5d',
          userGroupId: '8c2e1b4a-9d3f-4c5e-a6b7-c8d9e0f1a2b3',
          userGroupName: 'CtrlChain Europe',
          eventType: 'PickupEta',
          entityId: 'b9b6854f-282f-4e0d-a529-555f23441541',
          entityType: 'Order',
          responseStatusCode: 200,
        },
      ],
    },
  },
  'POST /api/v1/Webhook/retrigger': {
    id: 'webhooks-retrigger',
    title: 'Retrigger a webhook',
    description:
      'Retries the delivery of a previously triggered webhook event to your configured endpoint.',
    returns:
      'Whether the retriggered delivery succeeded, with the status code and message returned by your endpoint.',
    requestBodyExample: { webhookEventId: 'bc4fff3e-cfc3-0000-8d5d-000b12310e5d' },
    responseExample: {
      success: true,
      responseStatusCode: 200,
      responseMessage: 'OK',
    },
  },
};
